import AdminTechnologyCard from '@components/AdminTechnologyCard/AdminTechnologyCard.component';
import { Box, Button, InputAdornment, styled, TextField, Typography } from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SearchIcon from 'icons/Search.icon';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

interface Technology {
    id: string;
    name: string;
    description: string;
    field: number;
    updatedOnUtc: string;
}

interface TechnologyGroup {
    field: number;
    technologies: Technology[];
}

const StyledSearch = styled(TextField)({
  paddingTop: '8px',
  maxWidth: '720px',
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    '& fieldset': {
      borderColor: 'var(--Interviewer-Lightest-Black, #02051159)',
      
    },
    '&:hover fieldset': {
      borderColor: 'var(--Interviewer-Lightest-Black, #02051159)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--Interviewer-Lightest-Black, #02051159)',
    },
  },
});

const StyledButton = styled(Button)({
  padding: '6px 14px',
  borderRadius: '8px',
  background: 'var(--Lightest-Green)',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  color: 'var(--Interviewer-Green)',
});

export const Route = createFileRoute(
  '/(partner)/_partner/create-interview-step1',
)({
  component: RouteComponent,
})

function RouteComponent() {
    const [searchInput, setSearchInput] = useState('');
    const navigate = useNavigate();
    
    const { data: technologies, isLoading, error } = useQuery({
        queryKey: ['technologies'],
        queryFn: async () => {
            const token = localStorage.getItem('token');
            const response = await axios.get('https://interviewer.runasp.net/api/technologies', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data as Technology[];
        }
    });

    const { data: mainOrganizationTechnologies, isLoading: isLoadingMainOrg, error: errorMainOrg } = useQuery({
        queryKey: ['main-organization-technologies'],
        queryFn: async () => {
            const token = localStorage.getItem('token');
            const response = await axios.get('https://interviewer.runasp.net/api/technologies/main-organization', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data as TechnologyGroup[];
        }
    });

    const handleTechnologyClick = (technology: Technology, category: string) => {
        navigate({
            to: '/create-interview-step2',
            search: {
                category: category,
                techId: technology.id
            }
        });
    };

    return (
        <Box sx={{maxWidth: '80%', mx: 'auto'}}>
            <Box
                sx= {{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                }}
            >
                <Typography variant="h1">Select Technology</Typography>
                <Typography variant="h4" sx={{ textAlign: 'center'}}>Select the category from the list or search for a specific technology</Typography>

                <StyledSearch
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    fullWidth
                    type="search"
                    slotProps={{
                        input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        },
                    }}
                    placeholder="Search for a technology"
                />

                <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                    <StyledButton>Mobile</StyledButton>
                    <StyledButton>Backend</StyledButton>
                    <StyledButton>Frontend</StyledButton>
                </Box>
            </Box>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr',
                    md: 'repeat(2, 1fr)',
                },
                pt: '48px',
                gap: '10px',
                pb: '30px'
            }}>
                <Box>
                    <Typography variant='h2' sx={{pb: '20px', textAlign: 'center',}}>Technology 1</Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        maxHeight: '600px',
                        overflowY: 'auto',
                        pr: 1,
                    }}>
                        {isLoading && <Typography>Loading technologies...</Typography>}
                        {error && <Typography color="error">Error loading technologies</Typography>}
                        {technologies?.map((technology) => (
                            <Box 
                                key={technology.id} 
                                onClick={() => handleTechnologyClick(technology, 'all-technologies')}
                                sx={{ cursor: 'pointer' }}
                            >
                                <AdminTechnologyCard technology={technology} />
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box>
                    <Typography variant='h2' sx={{pb: '20px', textAlign: 'center'}}>Main Organization</Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        maxHeight: '600px',
                        overflowY: 'auto',
                        pr: 1,
                    }}>
                        {isLoadingMainOrg && <Typography>Loading main organization technologies...</Typography>}
                        {errorMainOrg && <Typography color="error">Error loading main organization technologies</Typography>}
                        {mainOrganizationTechnologies?.flatMap((group) => 
                            group.technologies.map((technology) => (
                                <Box 
                                    key={technology.id} 
                                    onClick={() => handleTechnologyClick(technology, 'main-organization')}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <AdminTechnologyCard technology={technology} />
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}